import { Component, OnDestroy, OnInit, TemplateRef, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Favorite } from './favorite.model';
import { FavoriteService } from './favorite.service';
import { Subject, Subscription } from 'rxjs';
import { StateService } from '../../shared/state/state.service';
import { AuthService } from '../../auth/auth.service';
import { WebsocketService } from '../../shared/websocket/websocket.service';
import { uniqBy } from 'lodash-es';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../../components/general/base/base.component';
import { NgbDropdownConfig } from '@ng-bootstrap/ng-bootstrap';

/**
 * Favorite component is used to store current view settings as new favorite and toggle these favorites.
 */
@Component({
  selector: 'qm-favorite',
  templateUrl: './favorite.component.html',
  styleUrls: ['./favorite.component.scss'],
  providers: [NgbDropdownConfig],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FavoriteComponent extends BaseComponent implements OnInit, OnDestroy {
  /**
   *  Favorites name
   */
  public name: string;
  /**
   * New favorite model instance
   */
  public favorite: Favorite;
  /**
   * List of favorites as Observable, https://angular.io/api/common/AsyncPipe
   */
  public favorites: Favorite[] = [];
  /**
   * Temp for tha favorite name
   */
  public tempName: string;
  /**
   * Observable for new websocket messages
   */
  private websocketChanges: Subscription;
  /**
   * Subscription manager
   * @type {Subject<any>}
   */
  private ngUnsubscribe: Subject<void> = new Subject();

  /**
   * Default constructor, inject dependencies
   */
  constructor(private favoriteService: FavoriteService,
              private changeDetector: ChangeDetectorRef,
              private stateService: StateService,
              private auth: AuthService,
              public config: NgbDropdownConfig,
              private websocket: WebsocketService) {
    super();
    this.name = 'FavoriteComponent';
    this.name = null;

    this.config.placement = 'bottom-right';
  }

  /**
   * Angular OnInit event hook
   */
  async ngOnInit() {
    this.name = '';
    this.websocketChanges = this.favoriteService.newWebsocketMessage$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(async data => {
      this.favorites = [];
      this.favorites = uniqBy(this.favorites.concat((await this.favoriteService.filter({
        global: true,
        withDeleted: false
      })).data), o => {
        return o.id;
      });
      this.changeDetector.markForCheck();
      this.favorites = uniqBy(this.favorites.concat((await this.favoriteService.filter({
        userId: this.auth.getSubject(),
        withDeleted: false
      })).data), o => {
        return o.id;
      });
      this.changeDetector.markForCheck();
    });
    this.favorites = uniqBy(this.favorites.concat((await this.favoriteService.filter({
      global: true
    })).data), o => {
      return o.id;
    });
    this.favorites = uniqBy(this.favorites.concat((await this.favoriteService.filter({
      userId: this.auth.getSubject()
    })).data), o => {
      return o.id;
    });
  }

  /**
   * Angular destructor function
   */
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Save current state as new favorite
   * @param {string} name
   * @param global
   */
  public beforeSave(name: string, global: boolean) {
    if (name) {
      this.favorite = new Favorite();
      this.favorite.global = global;
      this.favorite.userId = this.auth.getSubject();
      this.favorite.name = name;
      this.favorite.state = this.stateService.getStateEntriesAsString();
      this.saveView(this.favorite);
    }
  }

  public async updateFavorite(favorite: Favorite) {
    favorite.userId = this.auth.getSubject();
    favorite.state = this.stateService.getStateEntriesAsString();
    await this.favoriteService.edit(favorite);
    this.name = null;
  }

  public restoreView(favorite: Favorite) {
    this.stateService.reloadState(JSON.parse(favorite.state));
    location.reload();
  }

  /**
   * Save favorite
   * @param {Favorite} favorite
   */
  private async saveView(favorite: Favorite) {
    await this.favoriteService.create(favorite);
    this.name = null;
  }

  /**
   * Delete favorite
   * @param {Favorite} favorite
   */
  public deleteView(favorite: Favorite) {
    this.favoriteService.remove(favorite);
  }
}

